import { useCallback } from 'react'
import { Flex, MuiDatePicker, Text } from '@pinchenterprisesnpm/friday-ui'
import { FormikProps } from 'formik'
import { INewDuplicateModalFormValues } from '@src/features/shift-post-v2/modals/new-duplicate-modal/new-duplicate-modal/new-duplicate-modal.form'
import { INewEditModalFormValues } from '@src/features/shift-post-v2/modals/new-edit-modal/new-edit-modal/new-edit-modal.form'
import { IScheduleSectionFormValues } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/schedule-section.types'
import { CustomValidationError } from 'features/shift-post-v2/style'
import { getNewSelectedDateRange, isBeforeToday } from '@src/hooks/useDatePickerRange'
import { generateRecurringDays } from './recurring-day-picker.utils'

const { DatePickerRange } = MuiDatePicker

export interface IRecurringDayPickerProps {
  formik: FormikProps<
    INewDuplicateModalFormValues | INewEditModalFormValues | IScheduleSectionFormValues
  >
  isDisabled?: boolean
}

export const RecurringDayPicker = ({ formik, isDisabled }: IRecurringDayPickerProps) => {
  const { values, setValues, errors } = formik

  const handleDateRangeChange = useCallback(
    (_: unknown, selectedDay: Date) => {
      if (isBeforeToday(selectedDay)) return

      const selectedDateRange = {
        from: values.multiday_day_start,
        to: values.multiday_day_end,
      }
      const newSelectedDateRange = getNewSelectedDateRange(selectedDateRange, selectedDay)

      const days = generateRecurringDays({
        dayStart: newSelectedDateRange.from,
        dayEnd: newSelectedDateRange.to,
        hourStart: values.hour_start,
        hourEnd: values.hour_end,
        shiftBreak: values.break,
        payValue: values.pay_value,
        days: values.days,
        dateIds: values.date_ids,
      })

      setValues({
        ...values,
        multiday_day_start: newSelectedDateRange.from,
        multiday_day_end: newSelectedDateRange.to,
        days,
      })
    },
    [setValues, values]
  )

  const handleDateRangeReset = useCallback(() => {
    setValues({
      ...values,
      multiday_day_start: undefined,
      multiday_day_end: undefined,
      days: [],
    })
  }, [setValues, values])

  return (
    <Flex flexDirection='column'>
      <Text mb='xs' mt={0}>
        Select a Date Range
      </Text>
      <DatePickerRange
        range={{
          from: values.multiday_day_start,
          to: values.multiday_day_end,
        }}
        onSelectRange={handleDateRangeChange}
        onReset={handleDateRangeReset}
        isDisabled={isDisabled}
        disabledDays={() => false}
      />

      {errors.multiday_day_start && typeof errors.multiday_day_start === 'string' ? (
        <CustomValidationError mt='xs' mb={0}>
          {errors.multiday_day_start}
        </CustomValidationError>
      ) : null}
      {errors.multiday_day_end && typeof errors.multiday_day_end === 'string' ? (
        <CustomValidationError mt='xs' mb={0}>
          {errors.multiday_day_end}
        </CustomValidationError>
      ) : null}
    </Flex>
  )
}
